import { useState, useEffect, useRef, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from 'react-toastify'
import Server from "../../Shared/Server"
import { FaFileExport } from "react-icons/fa";
import Spinner from '../../Shared/Spinner'
import SelectField from "../../Shared/SelectField";
import {years_list, months_list, categories_list} from "../../Shared/List";

import {AgGridReact} from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const LessPaidSummary = (props) => {
    const current_user = JSON.parse(localStorage.getItem('user'))
    const billing_date  = current_user?.branch?.billing_date

    const [errors, setErrors] = useState({});
    const [LessPaid, setLessPaid] = useState([]);
    const [pinnedBottomRowData, setPinnedBottomRowData] = useState([]);
    const [yearOptions, setYearOptions] = useState([]);
    const [monthOptions, setMonthOptions] = useState([]);
    const [byOptions, setByOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [title, setTitle] = useState('');
    const gridRef = useRef();
    const navigate = useNavigate();
    const date = new Date().toISOString().slice(0, 10);

    const [formData, setFormData] = useState({
        year: billing_date?.year || date.year,
        month: billing_date?.month || date.month,
        by: 1,
    })
    const {year, month, by} = formData
    const fetchData = async (payload) => {
        setIsLoading(true)
        try {
            const response = await Server.get(`/un-paid/less-paid-summary?year=${payload.year}&month=${payload.month}&by=${payload.by}`)
            const years = []
            const months = []
            const categories = []

            years_list.map(year => (
                years.push({
                    id: year.id,
                    key: year.key,
                    name: year.name
                })
            ))

            months_list.map(month => (
                months.push({
                    id: month.id,
                    key: month.key,
                    name: month.name
                })
            ))

            categories_list.map(category => (
                categories.push({
                    id: category.id,
                    key: category.key,
                    name: category.name
                })
            ))

            setLessPaid(response?.data)
            setYearOptions(years)
            setMonthOptions(months)
            setByOptions(categories)
            setIsLoading(false)

            const output = response?.data.reduce((data, row) => {
                data.customers += row.customers;
                data.total_debit += parseFloat(row.total_debit);
                data.total_credit += parseFloat(row.total_credit);
                data.total_discount += parseFloat(row.total_discount);
                data.total_balance += parseFloat(row.total_balance);
                return data;
            }, {customers: 0, total_debit: 0.00, total_credit: 0.00, total_discount: 0, total_balance: 0.00});
            setPinnedBottomRowData([
                {
                    customers: output.customers,
                    total_debit: parseFloat(output.total_debit).toFixed(2),
                    total_credit: parseFloat(output.total_credit).toFixed(2),
                    total_discount: parseFloat(output.total_discount).toFixed(2),
                    total_balance: parseFloat(output.total_balance).toFixed(2),
                }
            ]);
        } catch (error) {
            setIsLoading(false)
            if(error?.response?.status === 401){
                localStorage.removeItem('user')
                navigate('/login')
            }else if (error?.response?.status === 422) {
                setErrors({})
                error.response?.data?.detail.map((err) => (
                    setErrors((prevState) => ({
                        ...prevState,
                        [err.loc[1]]: err.msg
                    }))
                ))
            }else {
                toast.error(error?.response?.data?.detail || 'Failed to load less paid summary')
            }
        }
    }

    useEffect(() => {
        const monthName = months_list[formData.month - 1].name
        const categoryName = categories_list[formData.by - 1].name
        setTitle(`${monthName}, ${year} Less Paid By ${categoryName}`);
        fetchData({year: year, month: month, by: categoryName})
    }, [])

    const columnDefs = useMemo(() => [
        {
            headerName: "#",
            filter: false,
            maxWidth: 70,
            sortable: true,
            valueFormatter: function (params) {
            if (isNaN(params.node.id)) {
                return "";
            }
            return `${Number(params.node.rowIndex) + 1}`;
            },
        },

        {
            headerName: "Collector",
            field: "collector_name",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Contact",
            field: "contact_number",
            sortable: true,
            filter: true,
        },
        {
            headerName: "Customers",
            field: "customers",
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: params => Number(params.value),
            comparator: (valueA, valueB) => valueA - valueB
        },
        {
            headerName: "Debit",
            field: "total_debit",
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: params => Number(params.value),
            comparator: (valueA, valueB) => valueA - valueB
        },
        {
            headerName: "Credit",
            field: "total_credit",
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: params => Number(params.value),
            comparator: (valueA, valueB) => valueA - valueB,
        },
        {
            headerName: "Discount",
            field: "total_discount",
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: params => Number(params.value),
            comparator: (valueA, valueB) => valueA - valueB
        },
        {
            headerName: "Balance",
            field: "total_balance",
            sortable: true,
            filter: 'agNumberColumnFilter',
            valueFormatter: params => Number(params.value),
            comparator: (valueA, valueB) => valueA - valueB
        },
    ], []);

    const onChange = (e) => {
        setFormData((prevState) => ({
            ...prevState,
            [e.target.name]: e.target.value
        }))

    }

    const onDoubleClick = (payload) => {
        navigate(`/less-paid-detail`, { state: {payload: payload}})
    }

    const onSubmit = (e) => {
        e.preventDefault()

        const monthName = months_list[formData.month - 1].name
        const categoryName = categories_list[formData.by - 1].name
        setTitle(`${monthName}, ${year} Less Paid By ${categoryName}`);
        const payload = {year, month, by: categoryName}
        fetchData(payload)

    }

    const defaultColDef = useMemo(() => {
        return {
            filterParams: {
                buttons: []
            }
        };
    }, []);

    const onFirstDataRendered = (params) => {
        params.api.autoSizeAllColumns();
    }

    const onGridSizeChanged = (params) => {
        params.api.autoSizeAllColumns();
    }

    let gridApi
    const onGridReady=(params)=>{
        gridApi=params.api
    }
      const onExportClick=()=>{
        gridApi.exportDataAsCsv({ fileName: `${title}.csv` })
    }

    const getRowStyle = (params) => {
        if (params.node.rowPinned) {
          return {
            backgroundColor: '#f8f9fa',
            fontWeight: 'bold',
            color: '#000',
          };
        }
        return {};
    };

    if(isLoading){
        return <Spinner/>
    }

  return (
    <>
        <form onSubmit={onSubmit}>
            <div className="row">
                <div className="col-md-2">
                    <SelectField
                        name="year"
                        value={parseInt(year)}
                        options={yearOptions}
                        onChange={onChange}
                        error={errors.year}
                    />
                </div>
                <div className="col-md-2">
                    <SelectField
                        name="month"
                        value={parseInt(month)}
                        options={monthOptions}
                        onChange={onChange}
                        error={errors.month}
                    />
                </div>
                <div className="col-md-2">
                    <SelectField
                        name="by"
                        value={parseInt(by)}
                        options={byOptions}
                        onChange={onChange}
                        error={errors.by}
                    />
                </div>
                <div className="col-md-4">
                    <input type="submit" className="btn btn-info mt-1" value="Apply Filter" style={{float: 'left'}}/>
                </div>
                <div className="col-md-2">
                    <button onClick={()=>onExportClick()} className="btn btn-success mb-2" style={{float: 'right'}}><FaFileExport/> &nbsp;Export</button>
                </div>
            </div>
            <br/>
        </form>
        <div className="card mt-0">
            <div className="card-header p-2 bg-info text-white d-flex justify-content-center">
                    <h3 className="card-title">{title}</h3>
            </div>
            <div className="card-body">
                <div className="row">
                    <div className="col-md-12">
                        <div className="ag-theme-alpine responsive-aggrid" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            onRowDoubleClicked={(e) =>{
                                if (e.node.rowPinned) return '';
                                if (e.data.customers < 1) return '';
                                onDoubleClick({
                                    year: parseInt(year),
                                    month: parseInt(month),
                                    collector_id: e.data.collector_id,
                                    collector_name: e.data.collector_name
                                })
                            }}
                            defaultColDef={defaultColDef}
                            onFirstDataRendered={onFirstDataRendered}
                            onGridSizeChanged={onGridSizeChanged}
                            suppressMenuHide={true}
                            rowData={LessPaid}
                            domLayout={"normal"}
                            rowSelection={"multiple"}
                            animateRows={true}
                            pagination={false}
                            onGridReady={onGridReady}
                            resizable={true}
                            enableCellTextSelection={true}
                            suppressCellFocus={true}
                            ref={gridRef}
                            pinnedBottomRowData={pinnedBottomRowData}
                            getRowStyle={getRowStyle}
                            columnDefs={columnDefs}
                        ></AgGridReact>{" "}
                    </div>
                    </div>
                </div>
            </div>
        </div>
      <br/>
    </>
  )
}

export default LessPaidSummary